import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { randomCorrelationId, thaiForMessageKey, type ImportIssue } from '@nurse-import-contract';
import {
  callNurseNotesImportSubmit,
  callNurseNotesImportValidate,
} from '@/services/nurseNotesImportCallables';
import {
  runClientNurseNotesImportPipeline,
  workbookBase64FromFile,
} from '@/services/imports/nurseNotesXlsxPipeline';

type Step =
  | 'idle'
  | 'parsing'
  | 'client_error'
  | 'server_validate'
  | 'review'
  | 'replace_confirm'
  | 'submitting'
  | 'success'
  | 'error';

function plainIssues(list: ImportIssue[]): string {
  return list
    .map((i) => {
      const t = thaiForMessageKey(i.messageKey);
      return `${t.title}: ${t.cause} — ${t.nextAction}`;
    })
    .join('\n');
}

export type NurseNotesXlsxImportPanelProps = {
  departmentId: string | null;
  reportingMonth: string;
  submittedByName: string | null;
  readOnly: boolean;
  onImportSuccess?: () => void;
};

export function NurseNotesXlsxImportPanel({
  departmentId,
  reportingMonth,
  submittedByName,
  readOnly,
  onImportSuccess,
}: NurseNotesXlsxImportPanelProps) {
  const panelId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>('idle');
  const [file, setFile] = useState<File | null>(null);
  const [clientIssues, setClientIssues] = useState<ImportIssue[]>([]);
  const [serverIssues, setServerIssues] = useState<ImportIssue[]>([]);
  const [recoverableWarnings, setRecoverableWarnings] = useState<ImportIssue[]>([]);
  const [validationToken, setValidationToken] = useState<string | null>(null);
  const [requiresReplace, setRequiresReplace] = useState(false);
  const [recoverableAck, setRecoverableAck] = useState(false);
  const [replaceAck, setReplaceAck] = useState(false);
  const [correlationId, setCorrelationId] = useState(() => randomCorrelationId());
  const [longWait, setLongWait] = useState(false);
  const [statusNote, setStatusNote] = useState<string | null>(null);

  useEffect(() => {
    if (step !== 'server_validate') {
      setLongWait(false);
      return;
    }
    const t = window.setTimeout(() => setLongWait(true), 60_000);
    return () => window.clearTimeout(t);
  }, [step]);

  const resetFlow = useCallback(() => {
    setStep('idle');
    setFile(null);
    setClientIssues([]);
    setServerIssues([]);
    setRecoverableWarnings([]);
    setValidationToken(null);
    setRequiresReplace(false);
    setRecoverableAck(false);
    setReplaceAck(false);
    setCorrelationId(randomCorrelationId());
    setStatusNote(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const onPickFile = async (f: File | null) => {
    if (!f || readOnly) return;
    setFile(f);
    setClientIssues([]);
    setServerIssues([]);
    setStatusNote(null);
    setStep('parsing');
    const res = await runClientNurseNotesImportPipeline(f);
    if (res.ok === false) {
      setClientIssues(res.errors);
      setStep('client_error');
      return;
    }
    if (!departmentId) {
      setClientIssues([
        {
          code: 'NNI_MONTH_INVALID',
          severity: 'fatal',
          messageKey: 'invalid_root',
          section: 'department',
        },
      ]);
      setStep('client_error');
      return;
    }
    setStep('server_validate');
    try {
      const b64 = await workbookBase64FromFile(f);
      const cid = randomCorrelationId();
      setCorrelationId(cid);
      const out = await callNurseNotesImportValidate({
        departmentId,
        reportingMonth,
        workbookBase64: b64,
        fileName: f.name,
        correlationId: cid,
      });
      if (out.ok === false) {
        const errs = (out.errors as ImportIssue[] | undefined) ?? [];
        setServerIssues(errs);
        setStep('error');
        return;
      }
      setValidationToken(typeof out.validationToken === 'string' ? out.validationToken : null);
      setRequiresReplace(out.requiresReplacementConfirm === true);
      const rw = (out.recoverableWarnings as ImportIssue[] | undefined) ?? [];
      setRecoverableWarnings(rw);
      setRecoverableAck(rw.length === 0);
      setReplaceAck(false);
      setStep('review');
    } catch (e) {
      console.error(e);
      setServerIssues([
        {
          code: 'NNI_INTERNAL',
          severity: 'fatal',
          messageKey: 'generic_error',
          section: 'network',
        },
      ]);
      setStep('error');
    }
  };

  const runSubmit = async (replaceConfirmed: boolean) => {
    if (!validationToken || readOnly) return;
    setStep('submitting');
    try {
      const cid = randomCorrelationId();
      setCorrelationId(cid);
      const out = await callNurseNotesImportSubmit({
        validationToken,
        replaceConfirmed,
        submittedByName,
        correlationId: cid,
      });
      if (out.ok === false) {
        const errs = (out.errors as ImportIssue[] | undefined) ?? [];
        setServerIssues(errs);
        setStep('error');
        return;
      }
      setStatusNote(
        typeof out.submissionIdHint === 'string' ? `บันทึกรหัสอ้างอิง: ${out.submissionIdHint}` : 'ส่งสำเร็จ',
      );
      setStep('success');
      onImportSuccess?.();
    } catch (e) {
      console.error(e);
      setServerIssues([
        { code: 'NNI_INTERNAL', severity: 'fatal', messageKey: 'generic_error', section: 'network' },
      ]);
      setStep('error');
    }
  };

  return (
    <section
      className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 space-y-3"
      aria-labelledby={`${panelId}-title`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 id={`${panelId}-title`} className="text-sm font-semibold text-slate-800">
          นำเข้าจาก Excel (XLSX){' '}
          <span className="font-normal text-slate-500">— MVP: บันทึกทางการพยาบาลเท่านั้น</span>
        </h4>
        <span className="text-xs text-slate-500" aria-live="polite">
          correlation: {correlationId}
        </span>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="block w-full text-sm text-slate-600"
        disabled={readOnly || step === 'parsing' || step === 'server_validate' || step === 'submitting'}
        onChange={(e) => void onPickFile(e.target.files?.[0] ?? null)}
      />

      {file && (
        <p className="text-xs text-slate-600">
          ไฟล์: <span className="font-medium">{file.name}</span>
        </p>
      )}

      {step === 'parsing' && <p className="text-sm text-slate-600">กำลังตรวจสอบไฟล์บนเครื่อง…</p>}

      {step === 'server_validate' && (
        <div className="text-sm text-slate-600 space-y-1" role="status">
          <p>กำลังตรวจสอบบนเซิร์ฟเวอร์…</p>
          {longWait && (
            <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              ระบบยังทำงานอยู่ อาจใช้เวลาสักครู่สำหรับไฟล์ใหญ่ กรุณารอและอย่ากดซ้ำ
            </p>
          )}
        </div>
      )}

      {step === 'client_error' && clientIssues.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900 whitespace-pre-line">
          {plainIssues(clientIssues)}
        </div>
      )}

      {step === 'review' && validationToken && (
        <div className="space-y-3">
          {recoverableWarnings.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
              <p className="font-medium mb-2">คำเตือน (อ่านก่อนส่ง)</p>
              <ul className="list-disc pl-5 space-y-1">
                {recoverableWarnings.map((w, i) => (
                  <li key={i}>{plainIssues([w])}</li>
                ))}
              </ul>
              <label className="mt-3 flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={recoverableAck}
                  onChange={(e) => setRecoverableAck(e.target.checked)}
                  className="mt-1"
                />
                <span>ยืนยันว่าอ่านคำเตือนแล้ว และยอมรับการส่งข้อมูลตามที่ตรวจได้</span>
              </label>
            </div>
          )}

          {requiresReplace ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded-full bg-amber-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                disabled={!recoverableAck}
                onClick={() => {
                  setReplaceAck(false);
                  setStep('replace_confirm');
                }}
              >
                ดำเนินการแทนที่ข้อมูลเดิม
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              disabled={!recoverableAck || step !== 'review'}
              onClick={() => void runSubmit(false)}
            >
              ส่งข้อมูลที่ตรวจแล้ว
            </button>
          )}
        </div>
      )}

      {step === 'replace_confirm' && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3 space-y-3 text-sm">
          <p className="font-semibold text-red-900">ยืนยันการแทนที่ข้อมูลเดิม</p>
          <p className="text-red-900">
            รอบ {reportingMonth} แผนกที่เลือกมีข้อมูลบันทึกทางการพยาบาลอยู่แล้ว การส่งจะทำเครื่องหมายชุดเดิมเป็นแฟ้มเก่า
            และบันทึกชุดใหม่แทน การกระทำนี้ไม่สามารถยกเลิกในระบบได้ทันที
          </p>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={replaceAck}
              onChange={(e) => setReplaceAck(e.target.checked)}
              className="mt-1"
            />
            <span>ยืนยันว่าต้องการแทนที่ข้อมูลเดิม</span>
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded-full bg-slate-200 px-4 py-2 text-sm font-medium text-slate-800"
              onClick={() => setStep('review')}
            >
              กลับ
            </button>
            <button
              type="button"
              className="rounded-full bg-red-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              disabled={!replaceAck || !recoverableAck}
              onClick={() => void runSubmit(true)}
            >
              ยืนยันแทนที่และส่ง
            </button>
          </div>
        </div>
      )}

      {step === 'submitting' && (
        <p className="text-sm text-slate-600" role="status">
          กำลังบันทึก…
        </p>
      )}

      {step === 'success' && (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900 space-y-2">
          <p className="font-medium">นำเข้าสำเร็จ</p>
          {statusNote && <p>{statusNote}</p>}
          <button type="button" className="text-sm underline font-medium" onClick={resetFlow}>
            นำเข้าไฟล์ใหม่
          </button>
        </div>
      )}

      {step === 'error' && serverIssues.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900 whitespace-pre-line space-y-2">
          {plainIssues(serverIssues)}
          <button type="button" className="text-sm underline font-medium" onClick={() => setStep('idle')}>
            ลองใหม่
          </button>
        </div>
      )}

    </section>
  );
}
